import * as React from 'react';

import cx from 'classnames';

import { translate } from '~l10n/client';
import { IconTimer } from '~shared/components/icons/large/IconTimer/IconTimer';
import { IconCheckmark } from '~shared/components/icons/micro/IconCheckmark/IconCheckmark';
import { Popover } from '~shared/components/Popover';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { TimerIconState } from '~shared/utils/timerIconState';

import { ResultsGridCornerMask } from './ResultsGridCornerMask';
import { TimerPopoverContent } from './TimerPopoverContent';
import { StudentTimerDisplayState } from './useStudentTimerStates';

export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    const minuteText =
      minutes === 1 ? translate('minute') : translate('minutes');
    const secondText =
      seconds === 1 ? translate('second') : translate('seconds');
    return `${minutes} ${minuteText} and ${seconds} ${secondText}`;
  } else if (minutes > 0) {
    const minuteText =
      minutes === 1 ? translate('minute') : translate('minutes');
    return `${minutes} ${minuteText}`;
  } else {
    const secondText =
      seconds === 1 ? translate('second') : translate('seconds');
    return `${seconds} ${secondText}`;
  }
}

export function getTimerTooltip(
  state: TimerIconState,
  timerState: StudentTimerDisplayState | undefined,
  quizTimerDurationSeconds: number
): string {
  const remaining = timerState?.remainingSeconds ?? 0;

  switch (state) {
    case 'hidden':
      return translate('Timer hidden');
    case 'not-started':
      return `${translate('Timer not started')}, ${formatDuration(
        quizTimerDurationSeconds
      )} remaining`;
    case 'paused':
      return `${translate('Timer paused at')} ${formatDuration(remaining)}`;
    case 'ended-early':
      return `${translate('Timer ended by teacher at')} ${formatDuration(
        remaining
      )}`;
    case 'completed':
      return `${translate('Quiz submitted with')} ${formatDuration(
        remaining
      )} remaining`;
    case 'active':
    case 'warning':
    case 'critical':
      return `${formatDuration(remaining)} ${translate('remaining')}`;
    case 'expired':
      return translate('Time expired');
    default:
      return '';
  }
}

interface CellProps {
  children: React.ReactNode;
  displayScore: React.ReactNode;
  showName: boolean;
  present: boolean;
  finished?: boolean;
  isRosteredRoom: boolean;
  isActivityInProgress: boolean;
  hasTimers?: boolean;
  showTimers?: boolean;
  timerState?: StudentTimerDisplayState;
  quizTimerDurationSeconds?: number;
  participantName?: string;
}

type Status = Pick<CellProps, 'present'>;

function StatusIndicator({ present }: Status): React.ReactElement {
  return present ? (
    <div className="w-4 h-4 bg-green-600 rounded-pill"></div>
  ) : (
    <div className="w-4 h-px bg-blue-800"></div>
  );
}

export function ResultsParticipantCell({
  children,
  showName,
  present,
  finished = false,
  displayScore: scoreDisplay,
  isRosteredRoom,
  isActivityInProgress,
  hasTimers,
  showTimers,
  timerState,
  quizTimerDurationSeconds = 0,
  participantName = '',
}: CellProps): React.ReactElement {
  const wrapperClass = cx(
    'relative flex flex-grow items-center justify-between h-full px-4 rounded-4px bg-white'
  );
  const nameTextClass = cx(
    'text-sm font-400 leading-8 text-blue-900',
    !showName && 'tracking-widest'
  );

  return (
    <div className={wrapperClass}>
      <ResultsGridCornerMask />
      {/* Column 1: Name */}
      <div className="flex items-center flex-1 min-w-0">
        {isActivityInProgress && isRosteredRoom && (
          <div data-testid="presence" className="flex-shrink-0">
            <StatusIndicator present={present} />
          </div>
        )}
        <div
          className={cx(
            'min-w-0 w-48 flex-1 text-left',
            isRosteredRoom && 'pl-4'
          )}
        >
          <span
            data-student-name
            className={cx(nameTextClass, 'truncate block')}
          >
            {showName ? children : '•••••••'}
          </span>
        </div>
      </div>
      {/* Column 2: Timer (timed quizzes) or Checkmark (non-timed quizzes, finished students only) */}
      <div className="flex items-center justify-center w-12 flex-shrink-0">
        {hasTimers ? (() => {
          const iconState: TimerIconState = showTimers
            ? timerState?.state ?? 'not-started'
            : 'hidden';
          const tooltip = getTimerTooltip(iconState, timerState, quizTimerDurationSeconds);
          return (
            <div className="flex items-center" data-testid="student-timer">
              {isActivityInProgress ? (
                <TooltipV2 label={tooltip}>
                  <div>
                    <IconTimer state={iconState} />
                  </div>
                </TooltipV2>
              ) : (
                <Popover
                  mode="click"
                  disableArrow
                  maxWidth={384}
                  content={
                    <TimerPopoverContent
                      studentName={participantName}
                      iconState={iconState}
                      remainingSeconds={timerState?.remainingSeconds ?? 0}
                      timerDurationSeconds={
                        timerState?.timerDurationSeconds ??
                        quizTimerDurationSeconds
                      }
                      startedAt={timerState?.startedAt}
                      finishedAt={timerState?.finishedAt}
                    />
                  }
                >
                  <div className="cursor-pointer">
                    <IconTimer state={iconState} />
                  </div>
                </Popover>
              )}
            </div>
          );
        })() : finished ? (
          <div className="flex items-center" data-testid="student-finished">
            <IconCheckmark />
          </div>
        ) : null}
      </div>
      {/* Column 3: Score */}
      <div className="flex items-center justify-end w-16 flex-shrink-0">
        <span
          data-student-score
          className="text-sm font-400 leading-8 text-blue-900 text-left block w-full px-2"
        >
          {scoreDisplay}
        </span>
      </div>
    </div>
  );
}
